import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useContext, memo } from "react";
import { pluck, pipe } from "ramda";
import { useOrganizationsContext } from "@webapi/contexts/organizations";
import cookies from "js-cookie";
import { sortUnitlists, selectUnitListCheckbox, removeUnitList, setUnitListProp, getUnitListIdx, volatileIdx, createNewUnitListFromSelected, getVolatileId, newUnitListEntry } from "@unitLists/utils";
import PropTypes from "prop-types";
import { SubText, Flex, RadioButton, SecondaryButton } from "@base-ui";
import { Accordion } from "@interdependence-ui";
import { ThemeContext } from "styled-components";
import { useUnitListsContext } from "@unitLists/context";
import IconButtonRow from "./IconButtonRow";
import initializeConfirm from "@domain-ui/dialogs/confirmDialog/initializeConfirm";
import initializeTextInput from "@domain-ui/dialogs/textInputPopup/initializeTextInput";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var SelectUnitListModule = /*#__PURE__*/memo(function () {
  var _useUnitListsContext = useUnitListsContext(),
    unitLists = _useUnitListsContext.unitLists,
    setUnitLists = _useUnitListsContext.setUnitLists;
  var _useOrganizationsCont = useOrganizationsContext(),
    lookupOrganization = _useOrganizationsCont.lookupOrganization;
  var theme = useContext(ThemeContext);
  var options = sortUnitlists(unitLists.map(function (x) {
    return {
      id: x.id,
      label: x.name,
      checked: x.selected,
      volatile: x.volatile,
      accessibleLabel: "Inneh\xE5ller: ".concat(pluck("id", x.units).map(function (id) {
        var _lookupOrganization;
        return (_lookupOrganization = lookupOrganization(id)) === null || _lookupOrganization === void 0 ? void 0 : _lookupOrganization.name;
      }).join(", "))
    };
  }));
  var createNewUnitlist = function createNewUnitlist(name) {
    setUnitLists(function (old) {
      var newEmptyList = newUnitListEntry([], old, true, false, name);
      // Make sure only the new list is selected
      var updatedOld = old.map(function (list) {
        return _objectSpread(_objectSpread({}, list), {}, {
          selected: false
        });
      });
      return [newEmptyList].concat(updatedOld);
    });
  };
  return /*#__PURE__*/_jsxs(Flex.Column, {
    mt: "10px",
    position: "relative",
    children: [/*#__PURE__*/_jsx(UnitFieldset, {
      id: "select-unitlist",
      options: options,
      name: "unitlist",
      updateSelected: function updateSelected(id, isChecked) {
        return setUnitLists(function (old) {
          return selectUnitListCheckbox(old, id, isChecked);
        });
      },
      background: theme.colors.i5,
      minHeight: "44px",
      padding: "0",
      borderTop: "1px solid ".concat(theme.colors.i1),
      borderBottom: false,
      ellipse: true
    }), /*#__PURE__*/_jsx(SecondaryButton, {
      id: "save-unitlist",
      onClick: function onClick() {
        return initializeTextInput({
          title: "Skapa ny enhetslista",
          id: "new-unitlist",
          submitLabel: "Spara enhetslista",
          label: "Fyll i ett namn för att skapa en ny lista...",
          placeholder: "Fyll i ett namn...",
          callback: function callback(name) {
            return createNewUnitlist(name);
          }
        });
      },
      m: "10px 20px 10px",
      background: theme.colors.P1,
      color: theme.colors.G1,
      children: "Skapa ny enhetslista"
    })]
  });
});
var UnitFieldset = function UnitFieldset(props) {
  var _useUnitListsContext2 = useUnitListsContext(),
    unitLists = _useUnitListsContext2.unitLists,
    setUnitLists = _useUnitListsContext2.setUnitLists;
  var theme = useContext(ThemeContext);
  var id = props.id,
    options = props.options,
    name = props.name,
    updateSelected = props.updateSelected,
    background = props.background,
    minHeight = props.minHeight,
    padding = props.padding,
    borderTop = props.borderTop,
    borderBottom = props.borderBottom,
    ellipse = props.ellipse;
  var handleCheckboxChange = function handleCheckboxChange(optionId, checked) {
    updateSelected(optionId, !checked);
  };
  var updateName = function updateName(id, newName) {
    setUnitLists(function (old) {
      return setUnitListProp("name", newName, getUnitListIdx(old, id), old);
    });
  };
  var handleUpdateList = function handleUpdateList(id, label) {
    initializeTextInput({
      title: "Uppdatera titel p\xE5 ".concat(label, "?"),
      id: "update-unitlist",
      submitLabel: "Ok",
      label: "Namnet på listan",
      defaultValue: "".concat(label),
      callback: function callback(newName) {
        return updateName(id, newName.trim());
      }
    });
  };
  var removeList = function removeList(id) {
    var updatedUnitLists = unitLists.filter(function (i) {
      return i.id !== id;
    });
    setUnitLists(function (old) {
      return removeUnitList(old, id);
    });
    cookies.set("visUnitLists",
    // ensure no volatile lists are saved
    JSON.stringify(updatedUnitLists.filter(function (list) {
      return !list.volatile;
    })), {
      expires: 34560000,
      secure: true
    });
  };
  var handleRemoveList = function handleRemoveList(id, label) {
    initializeConfirm({
      title: "Radera ".concat(label, "?"),
      ingress: "Vill du radera denna enhetslista?",
      subtext: "Raderad lista går inte att återskapa",
      confirmLabel: "Radera",
      variant: "danger",
      callback: function callback() {
        return removeList(id);
      }
    });
  };
  var copyUnitlist = function copyUnitlist(name) {
    setUnitLists(function (old) {
      var newList = createNewUnitListFromSelected(unitLists, name);
      setTimeout(function () {
        var focusElement = document.getElementById("select-unitlist-radio-".concat(getVolatileId(old)));
        if (focusElement) {
          focusElement.focus();
        }
      }, 0);
      return newList;
    });
  };
  var handleCopyList = function handleCopyList(label) {
    initializeTextInput({
      title: "Kopiera ".concat(label, "?"),
      id: "copy-unitlist",
      submitLabel: "Spara enhetslista",
      label: "Fyll i ett namn för att skapa en ny lista...",
      defaultValue: "".concat(label, " - kopia"),
      callback: function callback(name) {
        return copyUnitlist(name.trim());
      }
    });
  };
  var saveUnitlist = function saveUnitlist(id, name) {
    setUnitLists(function (old) {
      return pipe(setUnitListProp("name", name, volatileIdx(old)), setUnitListProp("volatile", false, volatileIdx(old)))(old);
    });
  };
  var handleSaveList = function handleSaveList(_ref) {
    var id = _ref.id,
      label = _ref.label;
    initializeTextInput({
      title: "Spara ".concat(label, "?"),
      id: "save-unitlist",
      submitLabel: "Spara",
      label: "Fyll i ett namn för att skapa en ny lista...",
      placeholder: "Fyll i ett namn...",
      callback: function callback(name) {
        return saveUnitlist(id, name);
      }
    });
  };
  var renderCheckboxes = options.map(function (option) {
    return /*#__PURE__*/_jsxs("div", {
      style: {
        position: "relative"
      },
      children: [/*#__PURE__*/_jsx(RadioButton, {
        id: "".concat(id, "-radio-").concat(option.id),
        label: option.label,
        name: name,
        checked: option.checked,
        onChange: function onChange() {
          return handleCheckboxChange(option.id, option.checked);
        },
        ellipse: ellipse,
        maxLabelWidth: !option.checked ? "unset" : !option.volatile ? "150px" : "220px"
      }), option.checked && /*#__PURE__*/_jsx(IconButtonRow, {
        option: option,
        handleUpdateList: handleUpdateList,
        handleRemoveList: handleRemoveList,
        handleCopyList: handleCopyList,
        handleSaveList: handleSaveList
      })]
    }, option.id);
  });
  var emptyMessage = !options.length && /*#__PURE__*/_jsx(SubText, {
    fontStyle: "italic",
    m: "0 0 0 20px",
    p: "0",
    color: theme.colors.G5,
    children: "Inga sparade listor"
  });
  return /*#__PURE__*/_jsxs(Accordion, {
    id: id,
    title: "Mina enhetslistor",
    icon: "list",
    _open: true,
    limitedHeight: true,
    background: background ? background : "none",
    minHeight: minHeight,
    padding: padding,
    borderTop: borderTop,
    borderBottom: borderBottom,
    children: [emptyMessage, /*#__PURE__*/_jsx(Flex.Column, {
      width: "inherit",
      children: renderCheckboxes
    })]
  });
};
UnitFieldset.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  updateSelected: PropTypes.func.isRequired,
  background: PropTypes.string,
  minHeight: PropTypes.string,
  padding: PropTypes.string,
  borderTop: PropTypes.string,
  borderBottom: PropTypes.bool,
  ellipse: PropTypes.bool
};